import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, ButtonGroup, Grid, GridProps, Stack, Typography } from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { useModal } from 'mui-modal-provider';

import { venueGetOfferStatisticsApi } from '../../api/offers/venueGetOfferStatisticsApi';
import { venueListOffersApi } from '../../api/offers/venueListOffersApi';
import Button from '../../components/Button/Button';
import ChipOptions from '../../components/ChipOptions/ChipOptions';
import { CalendarDialog } from '../../components/Dialogs';
import { CalendarIcon } from '../../components/Icons';
import PageTitle from '../../components/PageTitle/PageTitle';
import { OfferStatus, VenueListOffersFilter } from '../../graphql/API';
import { useListOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useFilter } from '../../hooks/useFilter';
import { useAppSelector } from '../../redux/hooks';
import icons from '../../themes/icons';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import OffersTable from '../../components/Tables/Offers/OffersTable';
import TikTokOfferCard from '../../components/TikTokOfferBanner/TikTokOfferCard';
import AddOffer from './components/AddOffer';
import OfferCard from './components/OfferCard';
import OfferCardSkeleton from './components/OfferCardSkeleton';

type OfferPageView = 'grid' | 'list';

const VIEW_STORAGE_KEY = 'offerPageView';

const commonGridProps = (key: string): GridProps => ({
  key,
  item: true,
  xs: 6,
  md: 4,
  lg: 3,
  xl: 2.4,
});

const Offer: React.FC = () => {
  const navigate = useNavigate();
  const venue = useAppSelector((state) => state.venue.value);
  const { showModal } = useModal();
  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const handleCalendarOpen = React.useCallback(
    () => showModal(CalendarDialog, { title: 'Calendar' }),
    [showModal],
  );

  const [view, setView] = React.useState<OfferPageView>(
    () => (localStorage.getItem(VIEW_STORAGE_KEY) as OfferPageView) || 'grid',
  );

  const handleViewChange = React.useCallback((nextView: OfferPageView) => {
    setView(nextView);
    localStorage.setItem(VIEW_STORAGE_KEY, nextView);
  }, []);

  const [filters, setFilters] = useFilter<Partial<VenueListOffersFilter>>({
    status: OfferStatus.active,
  });

  const { data, isLoading } = useListOfferQuery(filters);
  const { data: offerStatisticsData } = useQuery({
    queryKey: ['venueGetOfferStatistics', venue?.id || ''],
    queryFn: async ({ queryKey }) => {
      const [, venueId] = queryKey as [string, string];

      return venueGetOfferStatisticsApi({ venueID: Number(venueId) });
    },
    enabled: Boolean(venue?.id),
  });

  React.useEffect(() => {
    if (isOnlineBrand) navigate('/products');
  }, [isOnlineBrand, navigate]);

  // Backend's `totalDeletedOffers` in statistics can be unreliable, so we fetch
  // the cancelled+deleted offers explicitly and count them client-side.
  const { data: cancelledLikeOffers } = useQuery({
    queryKey: ['cancelled-deleted-offers-count', venue?.id],
    queryFn: () =>
      venueListOffersApi({
        venueID: venue?.id || '',
        statuses: [OfferStatus.cancelled, OfferStatus.deleted],
        limit: 500,
        offset: 0,
      }),
    enabled: Boolean(venue?.id),
  });

  const totalCancelledIncludingDeleted = cancelledLikeOffers?.length ?? 0;

  return (
    <Box
      padding={4}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        flex: 1,
        position: 'relative',
      }}
    >
      <Box>
        <PageTitle
          title='Your offer'
          sideComponent={
            <Button startIcon={<CalendarIcon />} onClick={handleCalendarOpen}>
              Calendar
            </Button>
          }
        />

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap={2}
          mt={2}
        >
          <ChipOptions
            name='offerStatus'
            options={[
              {
                label: `All`,
                value: '',
              },
              {
                label: `Active (${offerStatisticsData?.totalActiveOffers ?? '0'})`,
                value: OfferStatus.active,
              },
              {
                label: `In Review (${offerStatisticsData?.totalPendingOffers ?? '0'})`,
                value: OfferStatus.pending,
              },
              {
                label: `Cancelled (${totalCancelledIncludingDeleted})`,
                value: OfferStatus.cancelled,
              },
              {
                label: `Ended (${offerStatisticsData?.totalInactiveOffers ?? '0'})`,
                value: OfferStatus.inactive,
              },
            ]}
            defaultValue={
              filters.statuses?.includes(OfferStatus.cancelled)
                ? OfferStatus.cancelled
                : (filters.status ?? '')
            }
            onChange={({ value: v }) =>
              setFilters({
                status: v === OfferStatus.cancelled || !v ? undefined : (v as OfferStatus),
                statuses:
                  v === OfferStatus.cancelled
                    ? [OfferStatus.cancelled, OfferStatus.deleted]
                    : undefined,
              })
            }
          />

          <Stack direction='row' alignItems='center' gap={2} sx={{ flexShrink: 0 }}>
            {view === 'list' && (
              <Button
                component={Link}
                to='/offers/create'
                variant='contained'
                startIcon={<icons.AddIcon />}
                sx={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
              >
                Create new offer
              </Button>
            )}

            <ButtonGroup variant='outlined' size='medium' sx={{ flexShrink: 0 }}>
              <Button
                sx={{
                  border: '1px solid #EFEEEE',
                  ...(view === 'grid' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handleViewChange('grid')}
              >
                <icons.GridIconV2 />
              </Button>
              <Button
                sx={{
                  border: '1px solid #EFEEEE',
                  ...(view === 'list' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handleViewChange('list')}
              >
                <icons.ListIconV2 />
              </Button>
            </ButtonGroup>
          </Stack>
        </Stack>

        <Box mt={4} />
      </Box>

      <Box>
        {isLoading ? (
          <Grid container spacing={2}>
            {[...Array(4)].map((_, i) => (
              <Grid {...commonGridProps(`grid-item-${i}`)}>
                <OfferCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : view === 'list' ? (
          <OffersTable data={data} />
        ) : !filters.status && !filters.statuses?.length ? (
          <Stack spacing={5}>
            {(
              [
                { key: OfferStatus.active, label: 'Active' },
                { key: OfferStatus.pending, label: 'In Review' },
                { key: OfferStatus.cancelled, label: 'Cancelled' },
                { key: OfferStatus.inactive, label: 'Ended' },
              ] as const
            ).map(({ key, label }) => {
              const items = data.filter((d) =>
                key === OfferStatus.cancelled
                  ? d.status === OfferStatus.cancelled || d.status === OfferStatus.deleted
                  : d.status === key,
              );
              const isActive = key === OfferStatus.active;
              if (items.length === 0 && !isActive) return null;

              return (
                <Box key={key}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1A1C1E',
                      mb: 2,
                    }}
                  >
                    {label} ({items.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {isActive && (
                      <>
                        <Grid {...commonGridProps('add-offer')}>
                          <AddOffer />
                        </Grid>
                        <Grid {...commonGridProps('tiktok-promo')}>
                          <TikTokOfferCard />
                        </Grid>
                      </>
                    )}
                    {items.map((x) => (
                      <Grid {...commonGridProps(x.id)}>
                        <OfferCard data={x} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <Grid {...commonGridProps('add-offer')}>
              <AddOffer />
            </Grid>
            <Grid {...commonGridProps('tiktok-promo')}>
              <TikTokOfferCard />
            </Grid>
            {data.map((x) => (
              <Grid {...commonGridProps(x.id)}>
                <OfferCard data={x} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Offer;
